import React, { useState } from 'react';
import { useTheme } from '@mui/material/styles';
import {
    Box,
    Card,
    CardContent,
    Grid,
    Typography,
    Button,
    TextField,
    Switch,
    FormControlLabel,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    InputAdornment,
    Slider,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Tabs,
    Tab,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@mui/material';

// icons
import {
    IconSettings,
    IconShield,
    IconTrash,
    IconPlus,
    IconEdit,
    IconDownload,
    IconCheck,
    IconChevronDown,
    IconLock,
    IconCpu,
    IconCode,
    IconFolder,
    IconPackage
} from '@tabler/icons-react';

const JupyterSettings = () => {
    const theme = useTheme();
    const [tabValue, setTabValue] = useState(0);
    const [extensionDialog, setExtensionDialog] = useState(false);
    const [userDialog, setUserDialog] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);

    // Settings state
    const [settings, setSettings] = useState({
        // Server settings
        enableSpawner: true,
        defaultUrl: '/lab',
        shutdownOnLogout: true,
        idleTimeout: 60,
        maxServers: 5,
        // Resource limits
        cpuLimit: 2,
        cpuGuarantee: 0.5,
        memoryLimit: 4,
        memoryGuarantee: 1,
        // Security
        enableSSL: true,
        allowedImages: 'jupyter/minimal-notebook,jupyter/scipy-notebook',
        enableCORS: false,
        corsOrigins: '',
        // Features
        enableTerminal: true,
        enableUpload: true,
        maxFileSize: 100,
        enableSharing: false,
        enableExtensions: true
    });

    // Users
    const users = [
        {
            id: 1,
            username: 'john_doe',
            email: 'john@example.com',
            role: 'admin',
            lastActive: '2 hours ago',
            servers: 2,
            diskUsage: '3.2 GB'
        },
        {
            id: 2,
            username: 'jane_smith',
            email: 'jane@example.com',
            role: 'user',
            lastActive: '5 mins ago',
            servers: 1,
            diskUsage: '1.8 GB'
        },
        { id: 3, username: 'bob_johnson', email: 'bob@example.com', role: 'user', lastActive: 'Yesterday', servers: 0, diskUsage: '845 MB' }
    ];

    // Extensions
    const extensions = [
        { id: 1, name: 'JupyterLab Git', enabled: true, version: '0.41.0', description: 'Git integration for JupyterLab' },
        { id: 2, name: 'Variable Inspector', enabled: true, version: '3.0.9', description: 'Variable inspector for JupyterLab' },
        { id: 3, name: 'Debugger', enabled: false, version: '0.6.2', description: 'Visual debugger for Jupyter notebooks' },
        { id: 4, name: 'Code Formatter', enabled: true, version: '1.5.3', description: 'Code formatting for multiple languages' }
    ];

    // Environment presets
    const environmentPresets = [
        { name: 'Data Science', image: 'jupyter/datascience-notebook', description: 'Python, R, Julia with common data science packages' },
        { name: 'Machine Learning', image: 'jupyter/tensorflow-notebook', description: 'TensorFlow, Keras, PyTorch pre-installed' },
        { name: 'Minimal', image: 'jupyter/minimal-notebook', description: 'Basic Python environment' },
        { name: 'Spark', image: 'jupyter/pyspark-notebook', description: 'Apache Spark with Python support' }
    ];

    const handleSettingChange = (setting, value) => {
        setSettings((prev) => ({ ...prev, [setting]: value }));
    };

    const handleExtensionToggle = (extensionId) => {
        // Toggle extension enabled state
        console.log('Toggle extension:', extensionId);
    };

    return (
        <Box>
            <Card>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
                        <Tab label="Server Configuration" />
                        <Tab label="User Management" />
                        <Tab label="Extensions & Plugins" />
                        <Tab label="Environment Presets" />
                        <Tab label="Security & Access" />
                    </Tabs>
                </Box>

                <CardContent>
                    {/* Server Configuration Tab */}
                    {tabValue === 0 && (
                        <Box>
                            <Typography variant="h4" gutterBottom>
                                JupyterHub Server Configuration
                            </Typography>

                            <Grid container spacing={3}>
                                {/* General Settings */}
                                <Grid item xs={12}>
                                    <Accordion defaultExpanded>
                                        <AccordionSummary expandIcon={<IconChevronDown />}>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <IconSettings />
                                                <Typography variant="h5">General Settings</Typography>
                                            </Box>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12}>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={settings.enableSpawner}
                                                                onChange={(e) => handleSettingChange('enableSpawner', e.target.checked)}
                                                            />
                                                        }
                                                        label="Enable custom spawner options"
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <TextField
                                                        fullWidth
                                                        label="Default URL"
                                                        value={settings.defaultUrl}
                                                        onChange={(e) => handleSettingChange('defaultUrl', e.target.value)}
                                                        helperText="Default URL for new servers"
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <TextField
                                                        fullWidth
                                                        label="Idle Timeout"
                                                        type="number"
                                                        value={settings.idleTimeout}
                                                        onChange={(e) => handleSettingChange('idleTimeout', e.target.value)}
                                                        InputProps={{
                                                            endAdornment: <InputAdornment position="end">minutes</InputAdornment>
                                                        }}
                                                        helperText="Shutdown servers after idle time"
                                                    />
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={settings.shutdownOnLogout}
                                                                onChange={(e) => handleSettingChange('shutdownOnLogout', e.target.checked)}
                                                            />
                                                        }
                                                        label="Shutdown server on logout"
                                                    />
                                                </Grid>
                                            </Grid>
                                        </AccordionDetails>
                                    </Accordion>
                                </Grid>

                                {/* Resource Limits */}
                                <Grid item xs={12}>
                                    <Accordion>
                                        <AccordionSummary expandIcon={<IconChevronDown />}>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <IconCpu />
                                                <Typography variant="h5">Resource Limits</Typography>
                                            </Box>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Grid container spacing={3}>
                                                <Grid item xs={12} md={6}>
                                                    <Typography gutterBottom>CPU Limit: {settings.cpuLimit} cores</Typography>
                                                    <Slider
                                                        value={settings.cpuLimit}
                                                        onChange={(e, value) => handleSettingChange('cpuLimit', value)}
                                                        min={0.5}
                                                        max={8}
                                                        step={0.5}
                                                        marks
                                                        valueLabelDisplay="auto"
                                                    />
                                                    <Typography variant="caption" color="textSecondary">
                                                        Maximum CPU cores per user
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <Typography gutterBottom>CPU Guarantee: {settings.cpuGuarantee} cores</Typography>
                                                    <Slider
                                                        value={settings.cpuGuarantee}
                                                        onChange={(e, value) => handleSettingChange('cpuGuarantee', value)}
                                                        min={0.1}
                                                        max={2}
                                                        step={0.1}
                                                        marks
                                                        valueLabelDisplay="auto"
                                                    />
                                                    <Typography variant="caption" color="textSecondary">
                                                        Guaranteed CPU cores per user
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <Typography gutterBottom>Memory Limit: {settings.memoryLimit} GB</Typography>
                                                    <Slider
                                                        value={settings.memoryLimit}
                                                        onChange={(e, value) => handleSettingChange('memoryLimit', value)}
                                                        min={1}
                                                        max={16}
                                                        step={1}
                                                        marks
                                                        valueLabelDisplay="auto"
                                                    />
                                                    <Typography variant="caption" color="textSecondary">
                                                        Maximum memory per user
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <Typography gutterBottom>Memory Guarantee: {settings.memoryGuarantee} GB</Typography>
                                                    <Slider
                                                        value={settings.memoryGuarantee}
                                                        onChange={(e, value) => handleSettingChange('memoryGuarantee', value)}
                                                        min={0.5}
                                                        max={4}
                                                        step={0.5}
                                                        marks
                                                        valueLabelDisplay="auto"
                                                    />
                                                    <Typography variant="caption" color="textSecondary">
                                                        Guaranteed memory per user
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </AccordionDetails>
                                    </Accordion>
                                </Grid>

                                {/* Features */}
                                <Grid item xs={12}>
                                    <Accordion>
                                        <AccordionSummary expandIcon={<IconChevronDown />}>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <IconCode />
                                                <Typography variant="h5">Features</Typography>
                                            </Box>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} md={6}>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={settings.enableTerminal}
                                                                onChange={(e) => handleSettingChange('enableTerminal', e.target.checked)}
                                                            />
                                                        }
                                                        label="Enable terminal access"
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={settings.enableUpload}
                                                                onChange={(e) => handleSettingChange('enableUpload', e.target.checked)}
                                                            />
                                                        }
                                                        label="Enable file upload"
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={settings.enableSharing}
                                                                onChange={(e) => handleSettingChange('enableSharing', e.target.checked)}
                                                            />
                                                        }
                                                        label="Enable notebook sharing"
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={settings.enableExtensions}
                                                                onChange={(e) => handleSettingChange('enableExtensions', e.target.checked)}
                                                            />
                                                        }
                                                        label="Enable JupyterLab extensions"
                                                    />
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <TextField
                                                        fullWidth
                                                        label="Max Upload File Size"
                                                        type="number"
                                                        value={settings.maxFileSize}
                                                        onChange={(e) => handleSettingChange('maxFileSize', e.target.value)}
                                                        InputProps={{
                                                            endAdornment: <InputAdornment position="end">MB</InputAdornment>
                                                        }}
                                                        disabled={!settings.enableUpload}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </AccordionDetails>
                                    </Accordion>
                                </Grid>
                            </Grid>
                        </Box>
                    )}

                    {/* User Management Tab */}
                    {tabValue === 1 && (
                        <Box>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                                <Typography variant="h4">User Management</Typography>
                                <Button variant="contained" startIcon={<IconPlus />} onClick={() => setUserDialog(true)}>
                                    Add User
                                </Button>
                            </Box>

                            <TableContainer component={Paper} variant="outlined">
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Username</TableCell>
                                            <TableCell>Email</TableCell>
                                            <TableCell>Role</TableCell>
                                            <TableCell>Last Active</TableCell>
                                            <TableCell>Servers</TableCell>
                                            <TableCell>Disk Usage</TableCell>
                                            <TableCell align="right">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {users.map((user) => (
                                            <TableRow key={user.id}>
                                                <TableCell>{user.username}</TableCell>
                                                <TableCell>{user.email}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={user.role}
                                                        size="small"
                                                        color={user.role === 'admin' ? 'primary' : 'default'}
                                                    />
                                                </TableCell>
                                                <TableCell>{user.lastActive}</TableCell>
                                                <TableCell>{user.servers}</TableCell>
                                                <TableCell>{user.diskUsage}</TableCell>
                                                <TableCell align="right">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => {
                                                            setSelectedUser(user);
                                                            setUserDialog(true);
                                                        }}
                                                    >
                                                        <IconEdit size={16} />
                                                    </IconButton>
                                                    <IconButton size="small" color="error">
                                                        <IconTrash size={16} />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <Alert severity="info" sx={{ mt: 3 }}>
                                Users are synchronized with the main DataHub authentication system. Changes here will affect access across
                                all services.
                            </Alert>
                        </Box>
                    )}

                    {/* Extensions Tab */}
                    {tabValue === 2 && (
                        <Box>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                                <Typography variant="h4">JupyterLab Extensions</Typography>
                                <Button variant="contained" startIcon={<IconPlus />} onClick={() => setExtensionDialog(true)}>
                                    Install Extension
                                </Button>
                            </Box>

                            <Grid container spacing={3}>
                                {extensions.map((ext) => (
                                    <Grid item xs={12} md={6} key={ext.id}>
                                        <Card variant="outlined">
                                            <CardContent>
                                                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                                                    <Box>
                                                        <Typography variant="h6">{ext.name}</Typography>
                                                        <Typography variant="body2" color="textSecondary" gutterBottom>
                                                            Version {ext.version}
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ mt: 1 }}>
                                                            {ext.description}
                                                        </Typography>
                                                    </Box>
                                                    <Switch checked={ext.enabled} onChange={() => handleExtensionToggle(ext.id)} />
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>

                            <Card variant="outlined" sx={{ mt: 3 }}>
                                <CardContent>
                                    <Typography variant="h5" gutterBottom>
                                        Extension Settings
                                    </Typography>
                                    <FormControlLabel
                                        control={<Switch defaultChecked />}
                                        label="Allow users to install extensions"
                                        sx={{ mb: 2 }}
                                    />
                                    <FormControlLabel
                                        control={<Switch defaultChecked />}
                                        label="Enable extension manager in JupyterLab"
                                        sx={{ mb: 2 }}
                                    />
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={3}
                                        label="Blocked Extensions"
                                        placeholder="extension-name, another-extension"
                                        helperText="Comma-separated list of extensions to block"
                                    />
                                </CardContent>
                            </Card>
                        </Box>
                    )}

                    {/* Environment Presets Tab */}
                    {tabValue === 3 && (
                        <Box>
                            <Typography variant="h4" gutterBottom>
                                Environment Presets
                            </Typography>
                            <Alert severity="info" sx={{ mb: 3 }}>
                                Configure pre-built environments with specific packages and configurations for different use cases.
                            </Alert>

                            <Grid container spacing={3}>
                                {environmentPresets.map((preset) => (
                                    <Grid item xs={12} md={6} key={preset.name}>
                                        <Card variant="outlined">
                                            <CardContent>
                                                <Box display="flex" alignItems="center" gap={1} mb={2}>
                                                    <IconPackage />
                                                    <Typography variant="h6">{preset.name}</Typography>
                                                </Box>
                                                <Typography variant="body2" color="textSecondary" gutterBottom>
                                                    Image: {preset.image}
                                                </Typography>
                                                <Typography variant="body2">{preset.description}</Typography>
                                                <Box display="flex" gap={1} mt={2}>
                                                    <Button size="small" startIcon={<IconEdit />}>
                                                        Customize
                                                    </Button>
                                                    <Button size="small" startIcon={<IconDownload />}>
                                                        Export
                                                    </Button>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                                <Grid item xs={12} md={6}>
                                    <Card
                                        variant="outlined"
                                        sx={{
                                            height: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            minHeight: 200,
                                            cursor: 'pointer',
                                            '&:hover': {
                                                bgcolor: 'action.hover'
                                            }
                                        }}
                                    >
                                        <CardContent sx={{ textAlign: 'center' }}>
                                            <IconPlus size={48} style={{ opacity: 0.5 }} />
                                            <Typography variant="h6" sx={{ mt: 2, opacity: 0.7 }}>
                                                Create Custom Environment
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                        </Box>
                    )}

                    {/* Security Tab */}
                    {tabValue === 4 && (
                        <Box>
                            <Typography variant="h4" gutterBottom>
                                Security & Access Control
                            </Typography>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Box display="flex" alignItems="center" gap={1} mb={2}>
                                                <IconLock />
                                                <Typography variant="h5">SSL/TLS Settings</Typography>
                                            </Box>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={settings.enableSSL}
                                                        onChange={(e) => handleSettingChange('enableSSL', e.target.checked)}
                                                    />
                                                }
                                                label="Enable SSL/TLS"
                                                sx={{ mb: 2 }}
                                            />
                                            <TextField
                                                fullWidth
                                                label="SSL Certificate Path"
                                                disabled={!settings.enableSSL}
                                                sx={{ mb: 2 }}
                                            />
                                            <TextField fullWidth label="SSL Key Path" disabled={!settings.enableSSL} />
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Box display="flex" alignItems="center" gap={1} mb={2}>
                                                <IconShield />
                                                <Typography variant="h5">CORS Settings</Typography>
                                            </Box>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={settings.enableCORS}
                                                        onChange={(e) => handleSettingChange('enableCORS', e.target.checked)}
                                                    />
                                                }
                                                label="Enable CORS"
                                                sx={{ mb: 2 }}
                                            />
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={3}
                                                label="Allowed Origins"
                                                placeholder="https://example.com, https://app.example.com"
                                                disabled={!settings.enableCORS}
                                                value={settings.corsOrigins}
                                                onChange={(e) => handleSettingChange('corsOrigins', e.target.value)}
                                            />
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={12}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Box display="flex" alignItems="center" gap={1} mb={2}>
                                                <IconFolder />
                                                <Typography variant="h5">Allowed Docker Images</Typography>
                                            </Box>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={4}
                                                label="Allowed Images"
                                                value={settings.allowedImages}
                                                onChange={(e) => handleSettingChange('allowedImages', e.target.value)}
                                                helperText="Comma-separated list of allowed Docker images for user environments"
                                            />
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                        </Box>
                    )}
                </CardContent>
            </Card>

            {/* Save Button */}
            <Box display="flex" justifyContent="flex-end" mt={3} gap={2}>
                <Button variant="outlined">Reset to Defaults</Button>
                <Button variant="contained" startIcon={<IconCheck />}>
                    Save Settings
                </Button>
            </Box>

            {/* User Dialog */}
            <Dialog open={userDialog} onClose={() => setUserDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{selectedUser ? 'Edit User' : 'Add New User'}</DialogTitle>
                <DialogContent>
                    <TextField fullWidth label="Username" defaultValue={selectedUser?.username} sx={{ mt: 2, mb: 2 }} />
                    <TextField fullWidth label="Email" type="email" defaultValue={selectedUser?.email} sx={{ mb: 2 }} />
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Role</InputLabel>
                        <Select defaultValue={selectedUser?.role || 'user'} label="Role">
                            <MenuItem value="user">User</MenuItem>
                            <MenuItem value="admin">Admin</MenuItem>
                        </Select>
                    </FormControl>
                    {!selectedUser && (
                        <TextField
                            fullWidth
                            label="Initial Password"
                            type="password"
                            helperText="User will be prompted to change on first login"
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setUserDialog(false);
                            setSelectedUser(null);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button variant="contained">{selectedUser ? 'Update User' : 'Create User'}</Button>
                </DialogActions>
            </Dialog>

            {/* Extension Installation Dialog */}
            <Dialog open={extensionDialog} onClose={() => setExtensionDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Install JupyterLab Extension</DialogTitle>
                <DialogContent>
                    <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                        Extensions extend JupyterLab functionality. Only install trusted extensions.
                    </Alert>
                    <TextField fullWidth label="Extension Name or NPM Package" placeholder="@jupyterlab/git" sx={{ mb: 2 }} />
                    <TextField fullWidth multiline rows={3} label="Description" placeholder="What does this extension do?" />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setExtensionDialog(false)}>Cancel</Button>
                    <Button variant="contained" startIcon={<IconDownload />}>
                        Install Extension
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default JupyterSettings;
